import { caUtils } from '@mca/contentapi-js';
import * as logger from '../utils/logger';
import { setUser } from './user';
import {
  replaceUserPlaceholder,
  getBestMedia,
  replaceMacrosPlaceholders,
  getUniqueProfile
} from '../utils/medias';
import {
  toSeconds,
  isInsideCDNBuffer,
  setCdnBuffer,
  setIsStartOver,
  setStartTime,
  setContentUrl,
  setRestartUrl,
  setIsVspp,
  setOriginalRequest,
  setPlaylistDelay
} from './seek';
import { getRestartUrl, getToken, getUserItemsResumeData, getMediaUrlStb } from './gvp';
import { initYoubora, startSetup, createJoinTimeMark } from './youbora';
import { setSubtitles } from './audio-subtitles';
import { setContentResume } from './resume';
import { getKeyValues, getGvpData } from './config';
import { setTitle } from './bi';
import { streamType, youboraIntegrationLevel } from '../constants';
import {
  transparentProvider,
  commercializationTypes,
  resolution4K,
  quality
} from '../constants/types';

const log = logger.create('Player');
let transparentCatchupHBOHeader;

/**
 * Callback executed on LOAD event.
 *
 * @param {Object} loadRequestData Request received from the sender.
 * @returns {Object} Request modified with media information.
 */
export function load(loadRequestData) {
  log.info('Event onLoad received');
  log.info('Load Request', loadRequestData);

  if (
    loadRequestData.customData.type === streamType.vod ||
    loadRequestData.customData.type === streamType.recording
  ) {
    return loadRequestData.customData.isTrailer
      ? loadTrailer(loadRequestData)
      : loadVod(loadRequestData);
  } else {
    const date = new Date(loadRequestData.customData.liveStartTime);
    log.info('Load live start time (HH:MM:SS)', date.toLocaleTimeString());
    return loadLive(loadRequestData);
  }
}

/**
 * Loads a VOD playback
 *
 * @param {Object} loadRequestData Request data received.
 * @returns {Object} loadRequestData modified with media information.
 */
async function loadVod(loadRequestData) {
  // JoinTime start.
  createJoinTimeMark();
  return await playVod(loadRequestData);
}

/**
 * Loads a Live playback
 *
 * @param {Object} loadRequestData Request data received.
 * @returns {Object} loadRequestData modified.
 */
async function loadLive(loadRequestData) {
  // JoinTime start.
  createJoinTimeMark();
  setOriginalRequest(loadRequestData);
  if (loadRequestData.media.contentUrl) {
    log.info('Loading restart url', loadRequestData.media.contentUrl);
    return loadRequestData;
  } else {
    return await playLive(loadRequestData);
  }
}

/**
 * Loads a trailer playback
 *
 * @param {Object} loadRequestData Request data received.
 * @returns {Object} loadRequestData modified with media information.
 */
async function loadTrailer(loadRequestData) {
  return await playTrailer(loadRequestData);
}

/**
 * Plays live content without requesting data.
 * @param {Object} loadRequest Request data received.
 * @returns {Object} Modified load request.
 */
async function playLive(loadRequest) {
  const { liveChannel, liveSchedule, medias, itemType, user, audio } = loadRequest.customData;
  const auxMedias = [...medias];
  setUser(user);
  setIsVspp(false);
  setRestartUrl(null);
  auxMedias[0].url = replaceMacrosPlaceholders(
    replaceUserPlaceholder(auxMedias[0].url),
    true,
    liveChannel.isFastChannel && !liveChannel.hasPlaylistExperience
  );
  setTitle(liveSchedule.title);
  initYoubora();
  configureDRM(
    false,
    medias[0].drmLicenseServerUrl,
    null,
    null,
    liveChannel.isLatencyKey,
    liveChannel.hasPlaylistExperience && !liveChannel.isFastChannel,
    audio.split('/').length > 1,
    auxMedias[0].url.includes('.m3u8')
  );
  let request = {
    ...loadRequest,
    currentTime: Infinity,
    media: {
      ...loadRequest.media,
      contentUrl: medias[0].url,
      streamType: cast.framework.messages.StreamType.LIVE
    }
  };
  setContentUrl(medias[0].url);
  setCdnBuffer(liveChannel.cdnBuffer);
  setStartTime(liveSchedule.start);
  setIsStartOver(liveChannel.hasStartOver);
  setContentResume({
    callLetter: liveChannel.callLetter,
    id: liveChannel.id,
    mediaId: auxMedias[0].mediaId,
    quality: medias[0].quality
  });
  if (liveChannel.hasPlaylistExperience && !liveChannel.isFastChannel) {
    const delay = await getUserItemsResumeData(caUtils.getId(liveSchedule.pid));
    setPlaylistDelay(delay);
    log.info('Playlist delay', delay);
    request.currentTime = 0;
  } else if (!isInsideCDNBuffer(loadRequest.customData.liveStartTime) && liveChannel.hasStartOver) {
    let restartUrl = await getRestartUrl(
      loadRequest.customData,
      loadRequest.customData.liveStartTime
    );
    restartUrl = replaceMacrosPlaceholders(
      replaceUserPlaceholder(restartUrl),
      true,
      liveChannel.isFastChannel && !liveChannel.hasPlaylistExperience
    );
    restartUrl = getUniqueProfile(restartUrl, medias[0].quality);
    setIsVspp(true);
    setRestartUrl(restartUrl);
    request = {
      ...request,
      currentTime: 0,
      media: { ...request.media, contentUrl: restartUrl }
    };
  }
  startSetup({
    title: liveChannel.title,
    program: liveSchedule.title,
    duration: 0,
    resource: request.media.contentUrl,
    type: youboraIntegrationLevel.live,
    isLive: true,
    metadata: {
      contentId: liveChannel.id
    },
    quality: medias[0].quality,
    commercializationType:
      request.currentTime === 0 ? commercializationTypes.vspp : commercializationTypes.cdn,
    itemType: itemType
  });
  return request;
}

/**
 * Plays VoD content without requesting data.
 * @param {Object} loadRequest Request data received.
 * @returns {Object} Modified load request.
 */
async function playVod(loadRequest) {
  let auxMedias = [];
  const {
    id,
    title,
    transparentCatchupProvider,
    medias,
    subtitles,
    duration,
    commercializationType,
    itemType,
    user,
    audio
  } = loadRequest.customData;
  setUser(user);
  setTitle(title);
  initYoubora();
  setSubtitles(subtitles);
  // HBO or L7D.
  if (!medias[0].url) {
    const media = await getMedia(
      id,
      medias,
      cast.framework.CastReceiverContext.getInstance().canDisplayType(
        'video/mp4',
        null,
        resolution4K.width,
        resolution4K.height
      )
    );
    auxMedias = [media];
  } else if (
    medias.some((media) => media.quality === quality.HighDefinitionFull) &&
    cast.framework.CastReceiverContext.getInstance().canDisplayType(
      'video/mp4',
      null,
      resolution4K.width,
      resolution4K.height
    )
  ) {
    auxMedias = [...medias];
  } else {
    auxMedias = medias.filter((media) => media.quality !== quality.HighDefinitionFull);
  }
  auxMedias[0].url = replaceMacrosPlaceholders(
    replaceUserPlaceholder(auxMedias[0].url),
    false,
    commercializationType === commercializationTypes.avod
  );
  setContentResume({
    id,
    itemType,
    mediaId: auxMedias[0].mediaId,
    duration
  });
  configureDRM(
    true,
    auxMedias[0].drmLicenseServerUrl,
    auxMedias[0].customData,
    transparentCatchupProvider,
    false,
    false,
    audio.split('/').length > 1,
    auxMedias[0].url.includes('.m3u8')
  );
  startSetup({
    title,
    duration,
    type: youboraIntegrationLevel.vod,
    quality: auxMedias[0].quality,
    commercializationType,
    itemType,
    resource: auxMedias[0].url,
    isLive: false,
    metadata: {
      contentId: id
    }
  });
  return {
    ...loadRequest,
    media: {
      ...loadRequest.media,
      contentUrl: auxMedias[0].url
    }
  };
}

/**
 * Plays trailer content without requesting data.
 * @param {Object} loadRequest Request data received.
 * @returns {Object} Modified load request.
 */
async function playTrailer(loadRequest) {
  const { title, medias, user, audio } = loadRequest.customData;
  const auxMedias = [...medias];
  setUser(user);
  auxMedias[0].url = replaceUserPlaceholder(auxMedias[0].url);
  setTitle(title);
  configureDRM(
    false,
    auxMedias[0].drmLicenseServerUrl,
    null,
    null,
    false,
    false,
    audio.split('/').length > 1,
    auxMedias[0].url.includes('.m3u8')
  );
  return {
    ...loadRequest,
    media: {
      ...loadRequest.media,
      contentUrl: auxMedias[0].url
    }
  };
}

/**
 * Configures DRM customData with the user token.
 *
 * @param {Boolean} isVod VoD or live content.
 * @param {String} drmLicenseServerUrl DRM license server url.
 * @param {String?} customData licenseCustomData.
 * @param {number?} transparentCatchupProvider Transparent catchup provider.
 * @param {Boolean?} isLatencyKey Live content. Offset from live edge.
 * @param {Boolean?} isPlaylist Live content. Playlist.
 * @param {Boolean} isDolby Preferred audio codec Dolby Digital.
 * @param {Boolean} isHLS Content stream type.
 */
function configureDRM(
  isVod,
  drmLicenseServerUrl,
  customData,
  transparentCatchupProvider,
  isLatencyKey,
  isPlaylist,
  isDolby,
  isHLS
) {
  const {
    dashAutoResumeDuration,
    dashAutoResumeNumberOfSegments,
    dashInitialBandwidth,
    presentationDelay,
    dashLicenseTimeout,
    dashManifestTimeout,
    dashFragmentsTimeout,
    dashSwitchInterval,
    isDolbyDigitalPlusEnabled
  } = getKeyValues();
  const playerManager = cast.framework.CastReceiverContext.getInstance().getPlayerManager();
  let playbackConfig = Object.assign(
    new cast.framework.PlaybackConfig(),
    playerManager.getPlaybackConfig()
  );
  playbackConfig = {
    ...playbackConfig,
    licenseUrl:
      drmLicenseServerUrl || (isHLS ? getKeyValues().playreadyUrl : getKeyValues().widevineUrl),
    protectionSystem: isHLS
      ? cast.player.api.ContentProtection.PLAYREADY
      : cast.player.api.ContentProtection.WIDEVINE,
    autoResumeDuration: dashAutoResumeDuration,
    autoResumeNumberOfSegments: dashAutoResumeNumberOfSegments,
    licenseCustomData: isVod ? customData : getToken(),
    shakaConfig: {
      abr: {
        defaultBandwidthEstimate: dashInitialBandwidth,
        useNetworkInformation: false,
        switchInterval: dashSwitchInterval
      },
      manifest: {
        defaultPresentationDelay: isLatencyKey
          ? toSeconds(presentationDelay)
          : isPlaylist
          ? dashAutoResumeDuration * 6
          : dashAutoResumeDuration * 3,
        dash: {
          ignoreMinBufferTime: true,
          ignoreSuggestedPresentationDelay: true,
          // This value is needed because in SVOD shaka player corrects the drifting and playback
          // starts at a different frame on each launch.
          autoCorrectDrift: !isPlaylist
        }
      },
      preferredAudioCodecs:
        isDolbyDigitalPlusEnabled && isDolby ? ['ec-3', 'mp4a.40.2'] : ['mp4a.40.2']
    }
  };

  playbackConfig.licenseRequestHandler = (requestInfo) => {
    // Configure license timeout.
    requestInfo.timeoutInterval = dashLicenseTimeout;
    if (transparentCatchupProvider === transparentProvider.HBO) {
      requestInfo.headers['dt-custom-data'] = transparentCatchupHBOHeader;
    } else {
      // licenseCustomData currently not working
      requestInfo.headers['x-authentication'] = getToken();
    }
    return requestInfo;
  };

  playbackConfig.manifestHandler = (manifest) => {
    if (transparentCatchupProvider === transparentProvider.HBO) {
      var data = new DOMParser().parseFromString(manifest, 'application/xml');
      var contentProtection = data.querySelector('MPD ContentProtection');
      var keyID = contentProtection.getAttribute('cenc:default_KID');
      transparentCatchupHBOHeader = btoa(
        [
          customData,
          `keyid=${keyID}`,
          'operatorID=telefonica',
          `countryID=${getGvpData().country}`
        ].join('|')
      );
    } else {
      const regExp = /minBufferTime="([^"]*)"/;
      return manifest?.replace(regExp, 'minBufferTime="PT0S"');
    }
    return manifest;
  };

  // Configure Manifest timeouts.
  playbackConfig.manifestRequestHandler = (networkRequestInfo) => {
    networkRequestInfo.timeoutInterval = dashManifestTimeout;
    return networkRequestInfo;
  };

  // Configure Fragments timeouts.
  playbackConfig.segmentRequestHandler = (networkRequestInfo) => {
    networkRequestInfo.timeoutInterval = dashFragmentsTimeout;
    return networkRequestInfo;
  };

  playerManager.setPlaybackConfig(playbackConfig);
}

/**
 * Configures DRM customData with the user token.
 * @param {number} id Content ID.
 * @param {Array} medias Medias.
 * @param {Boolean} is4K 4K quality.
 * @returns {Object} Playable media.
 */
async function getMedia(id, medias, is4K) {
  const bestMedia = getBestMedia(medias, is4K);
  const { url, drmLicenseServerUrl, customData } = await getMediaUrlStb(id, bestMedia.mediaId);
  return {
    ...bestMedia,
    url,
    drmLicenseServerUrl,
    customData
  };
}
